import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { 
  RefreshCw, 
  CreditCard, 
  CheckCircle,
  AlertCircle,
  Loader2,
  ChevronRight,
  Star
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * RecurringPaymentBanner - Prominent banner to enable/manage recurring payments
 * Shows on dashboard for members with/without auto-renewal enabled
 */
const RecurringPaymentBanner = ({ member, onNavigateToPaymentMethods, onUpdate }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [autoRenewEnabled, setAutoRenewEnabled] = useState(false);
  const [hasDefaultCard, setHasDefaultCard] = useState(false);
  const [defaultCardInfo, setDefaultCardInfo] = useState(null);
  const [toggleLoading, setToggleLoading] = useState(false);

  useEffect(() => {
    fetchStatus();
  }, [member]);

  const fetchStatus = async () => {
    try {
      const [subscriptionRes, cardRes] = await Promise.all([
        axios.get(`${API}/subscriptions/status`).catch(() => ({ data: {} })),
        axios.get(`${API}/payment-methods/has-default`).catch(() => ({ data: { has_default: false } }))
      ]);
      
      setAutoRenewEnabled(subscriptionRes.data?.auto_renew_enabled || false);
      setHasDefaultCard(cardRes.data?.has_default || false);
      setDefaultCardInfo(cardRes.data?.card_info || null);
    } catch (error) {
      console.error('Error fetching status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleAutoRenew = async () => {
    // If enabling and no default card, prompt to add one
    if (!autoRenewEnabled && !hasDefaultCard) {
      toast.info(t('recurringPayment.addCardFirst'));
      if (onNavigateToPaymentMethods) {
        onNavigateToPaymentMethods();
      }
      return;
    }

    setToggleLoading(true);
    try {
      const response = await axios.post(`${API}/subscriptions/toggle-auto-renew`);
      const newStatus = response.data.auto_renew_enabled;
      setAutoRenewEnabled(newStatus);
      toast.success(
        newStatus 
          ? t('recurringPayment.enabledSuccess') 
          : t('recurringPayment.disabledSuccess')
      );
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error toggling auto-renew:', error);
      toast.error(error.response?.data?.detail || t('common.error'));
    } finally {
      setToggleLoading(false);
    }
  };

  if (loading) {
    return null; // Don't show while loading
  }

  // Don't show if member doesn't have a plan
  if (!member?.membership_plan_id) {
    return null;
  }

  return (
    <Card className={`border-2 ${autoRenewEnabled ? 'border-green-300 bg-gradient-to-r from-green-50 to-emerald-50' : 'border-amber-300 bg-gradient-to-r from-amber-50 to-orange-50'}`}>
      <CardContent className="p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          {/* Left side - Info */}
          <div className="flex items-start gap-4">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${autoRenewEnabled ? 'bg-green-100' : 'bg-amber-100'}`}>
              {autoRenewEnabled ? (
                <RefreshCw className="h-6 w-6 text-green-600" />
              ) : (
                <AlertCircle className="h-6 w-6 text-amber-600" />
              )}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-semibold text-gray-900">
                  {t('recurringPayment.title')}
                </h3>
                {autoRenewEnabled ? (
                  <Badge className="bg-green-500 text-xs">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    {t('recurringPayment.active')}
                  </Badge>
                ) : (
                  <Badge className="bg-amber-500 text-xs">
                    {t('recurringPayment.inactive')}
                  </Badge>
                )}
              </div>
              <p className="text-sm text-gray-600">
                {autoRenewEnabled 
                  ? t('recurringPayment.activeDescription')
                  : t('recurringPayment.inactiveDescription')
                }
              </p>
              
              {/* Show default card info if enabled */}
              {autoRenewEnabled && hasDefaultCard && defaultCardInfo && (
                <div className="mt-2 flex items-center gap-2 text-sm text-green-700 bg-white/50 px-3 py-1.5 rounded-lg inline-flex">
                  <CreditCard className="h-4 w-4" />
                  <span>{defaultCardInfo.brand} •••• {defaultCardInfo.last4}</span>
                  <Star className="h-3 w-3 text-amber-500" />
                </div>
              )}
              
              {/* Prompt to add card if trying to enable without one */}
              {!autoRenewEnabled && !hasDefaultCard && (
                <Button 
                  variant="link" 
                  size="sm" 
                  className="text-amber-700 hover:text-amber-800 p-0 h-auto mt-1"
                  onClick={onNavigateToPaymentMethods}
                >
                  {t('recurringPayment.addCardToEnable')}
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              )}
            </div>
          </div>

          {/* Right side - Toggle */}
          <div className="flex items-center gap-3 sm:flex-shrink-0">
            <Switch
              checked={autoRenewEnabled}
              onCheckedChange={handleToggleAutoRenew}
              disabled={toggleLoading}
              className="data-[state=checked]:bg-green-600"
            />
            {toggleLoading && (
              <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default RecurringPaymentBanner;
